import { create } from 'zustand';
import type {
  AlarmSite,
  ArcContact,
  ArcEvent,
  CidCode,
  ArcCallLog,
} from '@asi/shared';
import * as arcService from '@/services/arc';

type WsConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface ArcState {
  // Data
  contacts: ArcContact[];
  events: ArcEvent[];
  cidCodes: CidCode[];
  callLog: ArcCallLog[];
  
  // Selected
  selectedSite: AlarmSite | null;
  selectedEvent: ArcEvent | null;
  
  // UI State
  loading: boolean;
  polling: boolean;
  pollInterval: ReturnType<typeof setInterval> | null;
  
  // WebSocket State
  wsConnected: boolean;
  wsState: WsConnectionState;
  wsEventCount: number;
  
  // Stats
  stats: {
    total: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
    responseTimeAvg: number | null;
  } | null;
  pendingCount: number | null;

  // Contact Actions
  fetchContacts: (siteId: string) => Promise<void>;
  createContact: (contact: Omit<ArcContact, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateContact: (id: string, updates: Partial<ArcContact>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  reorderContacts: (siteId: string, contactIds: string[]) => Promise<void>;

  // Event Actions
  fetchEvents: (filters?: Parameters<typeof arcService.fetchArcEvents>[1]) => Promise<void>;
  reconcilePendingEvents: () => Promise<void>;
  acknowledgeEvent: (eventId: string) => Promise<void>;
  resolveEvent: (eventId: string, resolution: string) => Promise<void>;
  processEvent: (eventId: string, resolution?: string) => Promise<void>;
  selectEvent: (event: ArcEvent | null) => void;
  
  // WebSocket Event Actions
  addEventsFromWebSocket: (events: ArcEvent[]) => void;
  setWsState: (state: WsConnectionState) => void;
  incrementWsEventCount: (count?: number) => void;

  // CID Codes
  fetchCidCodes: () => Promise<void>;

  // Call Log
  fetchCallLog: (eventId: string) => Promise<void>;
  logCall: (call: Omit<ArcCallLog, 'id'>) => Promise<void>;

  // MQ Polling (legacy - now handled by gateway)
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
  pollOnce: () => Promise<void>;

  // Batch Process
  processBatch: (eventIds: string[], resolution: string) => Promise<void>;
  processBatchByFilter: (options: { siteId?: string; category?: string; resolution: string; eventIds?: string[] }) => Promise<void>;

  // Stats
  fetchStats: (startDate?: string, endDate?: string) => Promise<void>;
  fetchPendingCount: () => Promise<void>;

  // Site Selection
  setSite: (site: AlarmSite | null) => void;

  // Realtime
  subscribeToEvents: () => () => void;
}

export const useArcStore = create<ArcState>((set, get) => ({
  // Initial state
  contacts: [],
  events: [],
  cidCodes: [],
  callLog: [],
  selectedSite: null,
  selectedEvent: null,
  loading: false,
  polling: false,
  pollInterval: null,
  wsConnected: false,
  wsState: 'disconnected',
  wsEventCount: 0,
  stats: null,
  pendingCount: null,

  // Contact Actions
  fetchContacts: async (siteId: string) => {
    set({ loading: true });
    try {
      const contacts = await arcService.fetchContacts(siteId);
      set({ contacts });
    } finally {
      set({ loading: false });
    }
  },

  createContact: async (contact) => {
    const newContact = await arcService.createContact(contact);
    set((state) => ({ contacts: [...state.contacts, newContact] }));
  },

  updateContact: async (id, updates) => {
    const updated = await arcService.updateContact(id, updates);
    set((state) => ({
      contacts: state.contacts.map((c) => (c.id === id ? updated : c)),
    }));
  },

  deleteContact: async (id) => {
    await arcService.deleteContact(id);
    set((state) => ({
      contacts: state.contacts.filter((c) => c.id !== id),
    }));
  },

  reorderContacts: async (siteId, contactIds) => {
    await arcService.reorderContacts(siteId, contactIds);
    // Refetch to get updated priorities
    const contacts = await arcService.fetchContacts(siteId);
    set({ contacts });
  },

  // Event Actions
  fetchEvents: async (filters) => {
    set({ loading: true });
    try {
      const fetched = await arcService.fetchArcEvents(100, filters);
      // Merge: keep any in-flight realtime events not yet in DB result
      set((state) => {
        const fetchedIds = new Set(fetched.map((e) => e.id));
        const realtimeOnly = state.events.filter((e) => !fetchedIds.has(e.id));
        return { events: [...realtimeOnly, ...fetched].slice(0, 200) };
      });
    } finally {
      set({ loading: false });
    }
  },

  reconcilePendingEvents: async () => {
    try {
      const pending = await arcService.fetchPendingArcEvents();
      // Refresh the real DB count in parallel so the header badge stays accurate
      // even when the actual backlog exceeds the in-memory cap.
      get().fetchPendingCount();
      if (pending.length === 0) return;
      set((state) => {
        const existingIds = new Set(state.events.map((e) => e.id));
        const missing = pending.filter((e) => !existingIds.has(e.id));
        if (missing.length === 0) return state;
        return { events: [...missing, ...state.events].slice(0, 5000) };
      });
    } catch (err) {
      console.error('[ARC] reconcilePendingEvents error:', err);
    }
  },

  acknowledgeEvent: async (eventId) => {
    await arcService.acknowledgeArcEvent(eventId);
    set((state) => ({
      events: state.events.map((e) =>
        e.id === eventId
          ? { ...e, acknowledged_at: new Date().toISOString() }
          : e
      ),
    }));
  },

  resolveEvent: async (eventId, resolution) => {
    await arcService.resolveArcEvent(eventId, resolution);
    set((state) => ({
      events: state.events.map((e) =>
        e.id === eventId
          ? { ...e, resolved_at: new Date().toISOString(), resolution }
          : e
      ),
    }));
  },

  processEvent: async (eventId, resolution) => {
    await arcService.processArcEvent(eventId, resolution);
    const now = new Date().toISOString();
    set((state) => ({
      events: state.events.map((e) =>
        e.id === eventId
          ? { ...e, acknowledged_at: now, resolved_at: now, resolution: resolution ?? null }
          : e
      ),
      pendingCount: state.pendingCount !== null ? Math.max(0, state.pendingCount - 1) : null,
    }));
  },

  selectEvent: (event) => set({ selectedEvent: event }),

  processBatch: async (eventIds, resolution) => {
    await arcService.processBatchArcEvents(eventIds, resolution);
    const now = new Date().toISOString();
    set((state) => ({
      events: state.events.map((e) =>
        eventIds.includes(e.id)
          ? { ...e, acknowledged_at: now, resolved_at: now, resolution }
          : e
      ),
      pendingCount: state.pendingCount !== null ? Math.max(0, state.pendingCount - eventIds.length) : null,
    }));
    get().fetchPendingCount();
  },

  processBatchByFilter: async (options) => {
    await arcService.processBatchByFilter(options);
    // Patch store events that match the filter so the UI updates immediately
    const now = new Date().toISOString();
    set((state) => ({
      events: state.events.map((e) => {
        if (e.acknowledged_at) return e;
        if (options.siteId && e.site_id !== options.siteId) return e;
        if (options.category && options.category !== 'ALL' && e.event_category !== options.category) return e;
        return { ...e, acknowledged_at: now, resolved_at: now, resolution: options.resolution };
      }),
    }));
    // Refresh true count from server
    get().fetchPendingCount();
  },

  // WebSocket Event Actions
  addEventsFromWebSocket: (events: ArcEvent[]) => {
    set((state) => {
      const existingIds = new Set(state.events.map((e) => e.id));
      const newEvents = events.filter((e) => !existingIds.has(e.id));
      if (newEvents.length === 0) return state;
      const newPending = newEvents.filter((e) => !e.acknowledged_at).length;
      return {
        events: [...newEvents, ...state.events].slice(0, 200),
        pendingCount: state.pendingCount !== null ? state.pendingCount + newPending : null,
      };
    });
  },
  
  setWsState: (wsState: WsConnectionState) => {
    set({ 
      wsState, 
      wsConnected: wsState === 'connected',
    });
  },
  
  incrementWsEventCount: (count = 1) => {
    set((state) => ({ wsEventCount: state.wsEventCount + count }));
  },

  // CID Codes
  fetchCidCodes: async () => {
    const cidCodes = await arcService.fetchCidCodes();
    set({ cidCodes });
  },

  // Call Log
  fetchCallLog: async (eventId) => {
    const callLog = await arcService.fetchCallLog(eventId);
    set({ callLog });
  },

  logCall: async (call) => {
    const newCall = await arcService.logCall(call);
    set((state) => ({ callLog: [...state.callLog, newCall] }));
  },

  // MQ Polling
  startPolling: (intervalMs = 5000) => {
    const { pollOnce, pollInterval } = get();
    
    // Clear existing interval
    if (pollInterval) {
      clearInterval(pollInterval);
    }

    // Poll immediately
    pollOnce();

    // Set up interval
    const interval = setInterval(pollOnce, intervalMs);
    set({ polling: true, pollInterval: interval });
  },

  stopPolling: () => {
    const { pollInterval } = get();
    if (pollInterval) {
      clearInterval(pollInterval);
    }
    set({ polling: false, pollInterval: null });
  },

  pollOnce: async () => {
    try {
      const result = await arcService.pollMqMessages();
      
      if (result.batchId && result.list && result.list.length > 0) {
        // Process and save events to database
        const { saved, errors } = await arcService.processAndSaveMqEvents(
          result.list,
          result.batchId
        );
        
        // Confirm receipt with HPP
        await arcService.confirmMqBatch(result.batchId);
        
        // Refetch events to update UI
        await get().fetchEvents();
      }
    } catch (error) {
      console.error('[ARC] Poll error:', error);
    }
  },

  // Stats
  fetchStats: async (startDate, endDate) => {
    const stats = await arcService.getEventStats(startDate, endDate);
    set({ stats });
  },

  fetchPendingCount: async () => {
    const { count, error } = await (await import('@/services/supabase')).supabase
      .from('arc_events')
      .select('*', { count: 'exact', head: true })
      .is('acknowledged_at', null);
    if (!error) set({ pendingCount: count ?? 0 });
  },

  // Site Selection
  setSite: (site) => {
    set({ selectedSite: site, contacts: [] });
    if (site) {
      get().fetchContacts(site.id);
    }
  },

  // Realtime
  subscribeToEvents: () => {
    return arcService.subscribeToArcEvents(
      (event) => {
        set((state) => {
          if (state.events.some(e => e.id === event.id)) return state;
          return { events: [event, ...state.events].slice(0, 200) };
        });
      },
      (update) => {
        // Patch acknowledged_at/resolved_at when another view or the arc service updates the event
        set((state) => ({
          events: state.events.map(e =>
            e.id === update.id
              ? { ...e, acknowledged_at: update.acknowledged_at ?? e.acknowledged_at, resolved_at: update.resolved_at ?? e.resolved_at, resolution: update.resolution ?? e.resolution }
              : e
          ),
        }));
      },
    );
  },
}));
